<script setup lang="js">
import { ref, computed, onMounted, watch } from 'vue'
import { postWithToken, getAllWithToken } from '../../../lib/fetch-api'

// --- Element refs ---
const fileInput = ref(null)

// --- Reactive state ---
const files = ref([])
const category = ref('')
const subcategory = ref('')
const folder = ref('')
const displayName = ref('')
const error = ref(null)
const successMessage = ref('')
const isReviewing = ref(false)
const isUploading = ref(false)

// Options fetched from backend endpoints
const categoryOptions = ref([])
const folderOptions = ref([])

// Parsed-file state. Nothing here is written to the database.
const fileText = ref('')
const validation = ref(null)

// --- Computed values ---
const selectedFile = computed(() => files.value[0] || null)

const filteredSubcategories = computed(() => {
  if (!category.value) return []

  const selectedCat = categoryOptions.value.find(
    (cat) => cat.id === Number(category.value)
  )

  return selectedCat && selectedCat.subcategories
    ? selectedCat.subcategories.filter((sub) => sub.deleted_at === null)
    : []
})

const selectedCategoryName = computed(() => {
  const selected = categoryOptions.value.find(
    (cat) => cat.id === Number(category.value)
  )
  return selected?.name_en || ''
})

const selectedSubcategoryName = computed(() => {
  const selected = filteredSubcategories.value.find(
    (sub) => sub.id === Number(subcategory.value)
  )
  return selected?.name_en || ''
})

const selectedFolderName = computed(() => {
  const selected = folderOptions.value.find(
    (item) => item.id === Number(folder.value)
  )
  return selected?.name || ''
})

const canReview = computed(() => {
  return Boolean(
    selectedFile.value &&
      validation.value &&
      validation.value.validPointCount > 0 &&
      category.value &&
      subcategory.value &&
      folder.value &&
      displayName.value.trim() &&
      !isUploading.value
  )
})

const previewPoints = computed(() => {
  if (!validation.value?.validPoints?.length) return []

  const points = validation.value.validPoints
  const maximumPreviewPoints = 500

  if (points.length <= maximumPreviewPoints) {
    return points
  }

  const step = (points.length - 1) / (maximumPreviewPoints - 1)

  return Array.from({ length: maximumPreviewPoints }, (_, index) => {
    return points[Math.round(index * step)]
  })
})

const previewPolyline = computed(() => {
  const points = previewPoints.value

  if (points.length === 0) return ''

  const width = 760
  const height = 260
  const padding = 24

  const xValues = points.map((point) => point.x)
  const yValues = points.map((point) => point.y)

  const xMin = Math.min(...xValues)
  const xMax = Math.max(...xValues)
  const yMin = Math.min(...yValues)
  const yMax = Math.max(...yValues)

  const xRange = xMax - xMin || 1
  const yRange = yMax - yMin || 1

  return points
    .map((point) => {
      const x =
        padding +
        ((point.x - xMin) / xRange) * (width - padding * 2)

      const y =
        height -
        padding -
        ((point.y - yMin) / yRange) * (height - padding * 2)

      return `${x.toFixed(2)},${y.toFixed(2)}`
    })
    .join(' ')
})

// --- Data fetching ---
const fetchCategories = async () => {
  const response = await getAllWithToken('categories', null)

  if (response.isSuccess) {
    categoryOptions.value = response.body
  } else {
    error.value = response.message
  }
}

const fetchFolders = async () => {
  const response = await getAllWithToken('folders', null)

  if (response.isSuccess) {
    folderOptions.value = response.body
  } else {
    error.value = response.message
  }
}

onMounted(async () => {
  await fetchCategories()
  await fetchFolders()
})

watch(category, () => {
  // Prevent a stale subcategory from remaining selected after category changes.
  subcategory.value = ''
  isReviewing.value = false
})

watch([subcategory, folder, displayName], () => {
  isReviewing.value = false
})

// --- File parsing and validation ---
const cleanDisplayName = (fileName) => {
  return (fileName || '').replace(/\.(csv|txt)$/i, '').trim()
}

const resetParsedFileState = () => {
  fileText.value = ''
  validation.value = null
  isReviewing.value = false
}

const delimiterLabel = (delimiter) => {
  if (delimiter === '\t') return 'Tab'
  if (delimiter === ';') return 'Semicolon'
  return 'Comma'
}

const detectDelimiter = (content) => {
  const nonEmptyLines = content
    .split(/\r?\n/)
    .filter((line) => line.trim() !== '')

  const candidates = [',', '\t', ';']
  let bestDelimiter = ','
  let bestNumericRows = -1
  let bestStructuredRows = -1

  for (const delimiter of candidates) {
    let numericRows = 0
    let structuredRows = 0

    for (const line of nonEmptyLines) {
      const parts = line.split(delimiter)

      if (parts.length < 2) {
        continue
      }

      structuredRows += 1

      const xText = parts[0].trim().replace(/^\uFEFF/, '')
      const yText = parts[1].trim()

      if (!xText || !yText) {
        continue
      }

      const x = Number(xText)
      const y = Number(yText)

      if (Number.isFinite(x) && Number.isFinite(y)) {
        numericRows += 1
      }
    }

    if (
      numericRows > bestNumericRows ||
      (numericRows === bestNumericRows &&
        structuredRows > bestStructuredRows)
    ) {
      bestDelimiter = delimiter
      bestNumericRows = numericRows
      bestStructuredRows = structuredRows
    }
  }

  return bestDelimiter
}

const analyzeFileText = (content) => {
  const delimiter = detectDelimiter(content)

  const nonEmptyLines = content
    .split(/\r?\n/)
    .map((line, index) => ({
      lineNumber: index + 1,
      text: line,
    }))
    .filter((line) => line.text.trim() !== '')

  const validPoints = []
  const invalidRows = []

  for (const row of nonEmptyLines) {
    const parts = row.text.split(delimiter)

    if (parts.length < 2) {
      invalidRows.push({
        lineNumber: row.lineNumber,
        text: row.text,
      })
      continue
    }

    const xText = parts[0].trim().replace(/^\uFEFF/, '')
    const yText = parts[1].trim()

    if (!xText || !yText) {
      invalidRows.push({
        lineNumber: row.lineNumber,
        text: row.text,
      })
      continue
    }

    const x = Number(xText)
    const y = Number(yText)

    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      invalidRows.push({
        lineNumber: row.lineNumber,
        text: row.text,
      })
      continue
    }

    validPoints.push({
      lineNumber: row.lineNumber,
      x,
      y,
    })
  }

  const xValues = validPoints.map((point) => point.x)
  const xCounts = new Map()

  for (const x of xValues) {
    xCounts.set(x, (xCounts.get(x) || 0) + 1)
  }

  let duplicateXCount = 0

  for (const count of xCounts.values()) {
    if (count > 1) {
      duplicateXCount += count - 1
    }
  }

  let xOrder = 'N/A'

  if (validPoints.length >= 2) {
    let ascending = true
    let descending = true

    for (let index = 1; index < validPoints.length; index += 1) {
      if (validPoints[index].x < validPoints[index - 1].x) {
        ascending = false
      }

      if (validPoints[index].x > validPoints[index - 1].x) {
        descending = false
      }
    }

    if (ascending && descending) {
      xOrder = 'Constant'
    } else if (ascending) {
      xOrder = 'Ascending'
    } else if (descending) {
      xOrder = 'Descending'
    } else {
      xOrder = 'Mixed'
    }
  }

  return {
    delimiter,
    delimiterName: delimiterLabel(delimiter),
    totalNonEmptyRows: nonEmptyLines.length,
    validPointCount: validPoints.length,
    invalidRowCount: invalidRows.length,
    duplicateXCount,
    xMin: xValues.length ? Math.min(...xValues) : null,
    xMax: xValues.length ? Math.max(...xValues) : null,
    xOrder,
    validPoints,
    previewRows: validPoints.slice(0, 10),
    invalidRows: invalidRows.slice(0, 5),
  }
}

const handleFileChange = (event) => {
  error.value = null
  successMessage.value = ''
  resetParsedFileState()

  const selected = Array.from(event.target.files || [])
  files.value = selected.slice(0, 1)

  if (!selectedFile.value) {
    displayName.value = ''
    return
  }

  const fileName = selectedFile.value.name || ''
  const supportedFile = /\.(csv|txt)$/i.test(fileName)

  if (!supportedFile) {
    error.value = 'Please select a .csv or .txt spectral file.'
    files.value = []
    displayName.value = ''

    if (fileInput.value) {
      fileInput.value.value = ''
    }

    return
  }

  displayName.value = cleanDisplayName(fileName)

  const reader = new FileReader()

  reader.onload = (eventResult) => {
    const content = String(eventResult.target?.result || '')
    fileText.value = content
    validation.value = analyzeFileText(content)

    if (validation.value.validPointCount === 0) {
      error.value =
        'No valid X/Y data points were found. The importer accepts comma-, tab-, or semicolon-separated numeric values in the first two columns.'
    }
  }

  reader.onerror = () => {
    resetParsedFileState()
    error.value = 'The selected file could not be read.'
  }

  reader.readAsText(selectedFile.value)
}

// Helper to convert a File object to a Base64 string (without data URL prefix)
const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      const result = reader.result
      const base64String = result.split(',')[1]
      resolve(base64String)
    }

    reader.onerror = (readerError) => reject(readerError)
    reader.readAsDataURL(file)
  })
}

// --- Two-stage import flow ---
const reviewImport = () => {
  error.value = null
  successMessage.value = ''

  if (!selectedFile.value) {
    error.value = 'Please select a file to upload.'
    return
  }

  if (!validation.value || validation.value.validPointCount === 0) {
    error.value = 'The selected file does not contain any valid X/Y data points.'
    return
  }

  if (!category.value || !subcategory.value || !folder.value) {
    error.value = 'Please select a category, subcategory, and folder.'
    return
  }

  if (!displayName.value.trim()) {
    error.value = 'Please enter a display name.'
    return
  }

  isReviewing.value = true
}

const cancelReview = () => {
  isReviewing.value = false
}

const resetForm = () => {
  files.value = []
  category.value = ''
  subcategory.value = ''
  folder.value = ''
  displayName.value = ''
  fileText.value = ''
  validation.value = null
  isReviewing.value = false

  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const confirmImport = async () => {
  if (!canReview.value || !selectedFile.value) {
    error.value = 'Please complete and review the import information first.'
    return
  }

  isUploading.value = true
  error.value = null
  successMessage.value = ''

  try {
    const fileContentBase64 = await convertFileToBase64(selectedFile.value)

    // No database-schema change:
    // - file_name keeps the original uploaded filename
    // - description stores the user-facing display name
    const payload = {
      folder_id: folder.value,
      subcategory_id: subcategory.value,
      file_name: selectedFile.value.name,
      file_content: fileContentBase64,
      description: displayName.value.trim(),
      is_public: false,
    }

    const response = await postWithToken('spectfiles/upload', payload)

    if (response.isSuccess) {
      const importedName = displayName.value.trim()
      const importedPoints = validation.value.validPointCount

      successMessage.value =
        `${importedName} was imported successfully with ${importedPoints.toLocaleString()} valid data points.`

      resetForm()
      await fetchFolders()
      await fetchCategories()
    } else {
      error.value = response.message || 'Upload failed.'
    }
  } catch (uploadError) {
    error.value = `Upload failed: ${uploadError.message}`
  } finally {
    isUploading.value = false
  }
}
</script>

<template>
  <div class="p-8 max-w-5xl mx-auto bg-white rounded-lg min-h-screen flex flex-col space-y-8">
    <div>
      <h2 class="text-3xl font-bold text-blue-700 text-center">
        Import Spectral File
      </h2>
      <p class="mt-2 text-center text-sm text-gray-600">
        Select and validate the spectrum first. Nothing is saved until you confirm the import.
      </p>
    </div>

    <!-- Messages -->
    <div v-if="error" class="bg-red-100 text-red-700 p-3 rounded">
      {{ error }}
    </div>

    <div v-if="successMessage" class="bg-green-100 text-green-800 p-3 rounded">
      {{ successMessage }}
    </div>

    <!-- File Input -->
    <div>
      <label class="text-blue-700 block mb-2 font-medium">
        1. Select spectral file
      </label>

      <input
        ref="fileInput"
        type="file"
        accept=".csv,.txt,text/csv,text/plain"
        :disabled="isUploading"
        @change="handleFileChange"
        class="block w-full p-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
      />

      <p class="mt-2 text-xs text-gray-500">
        Supported format: .csv or .txt with numeric X and Y values in the first two columns. Comma, tab, and semicolon delimiters are detected automatically.
      </p>
    </div>

    <!-- Validation & Preview -->
    <div v-if="validation" class="space-y-5">
      <div class="rounded-lg border border-gray-200 bg-gray-50 p-5">
        <div class="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 class="text-lg font-semibold text-gray-900">
              File validation
            </h3>
            <p class="text-sm text-gray-600">
              {{ selectedFile?.name }}
            </p>
          </div>

          <span
            v-if="validation.validPointCount > 0"
            class="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800"
          >
            Ready for review
          </span>

          <span
            v-else
            class="rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800"
          >
            Cannot import
          </span>
        </div>

        <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div class="rounded-md border border-gray-200 bg-white p-3">
            <p class="text-xs text-gray-500">Valid points</p>
            <p class="text-lg font-semibold text-gray-900">
              {{ validation.validPointCount.toLocaleString() }}
            </p>
          </div>

          <div class="rounded-md border border-gray-200 bg-white p-3">
            <p class="text-xs text-gray-500">Skipped rows</p>
            <p class="text-lg font-semibold text-gray-900">
              {{ validation.invalidRowCount.toLocaleString() }}
            </p>
          </div>

          <div class="rounded-md border border-gray-200 bg-white p-3">
            <p class="text-xs text-gray-500">Duplicate X values</p>
            <p class="text-lg font-semibold text-gray-900">
              {{ validation.duplicateXCount.toLocaleString() }}
            </p>
          </div>

          <div class="rounded-md border border-gray-200 bg-white p-3">
            <p class="text-xs text-gray-500">X order</p>
            <p class="text-lg font-semibold text-gray-900">
              {{ validation.xOrder }}
            </p>
          </div>

          <div class="rounded-md border border-gray-200 bg-white p-3">
            <p class="text-xs text-gray-500">Detected delimiter</p>
            <p class="text-lg font-semibold text-gray-900">
              {{ validation.delimiterName }}
            </p>
          </div>
        </div>

        <div
          v-if="validation.validPointCount > 0"
          class="mt-4 grid gap-3 md:grid-cols-2"
        >
          <div class="rounded-md border border-gray-200 bg-white p-3">
            <p class="text-xs text-gray-500">X minimum</p>
            <p class="font-medium text-gray-900">
              {{ validation.xMin }}
            </p>
          </div>

          <div class="rounded-md border border-gray-200 bg-white p-3">
            <p class="text-xs text-gray-500">X maximum</p>
            <p class="font-medium text-gray-900">
              {{ validation.xMax }}
            </p>
          </div>
        </div>

        <div
          v-if="validation.invalidRowCount > 0"
          class="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          {{ validation.invalidRowCount }} nonempty row(s) will be skipped because the detected delimiter does not produce valid numeric X/Y values in the first two columns.
          A text header is therefore counted as a skipped row.
        </div>

        <div
          v-if="validation.duplicateXCount > 0"
          class="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          Duplicate X values were detected. They will be imported exactly as they appear in the file.
        </div>

        <div
          v-if="validation.xOrder === 'Mixed'"
          class="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          The X values are not consistently ascending or descending. The data will not be reordered during import.
        </div>
      </div>

      <!-- Spectrum preview -->
      <div
        v-if="validation.validPointCount > 0"
        class="rounded-lg border border-gray-200 bg-white p-5"
      >
        <h3 class="mb-3 text-lg font-semibold text-gray-900">
          Spectrum preview
        </h3>

        <div class="overflow-hidden rounded-md border border-gray-200 bg-gray-50">
          <svg
            viewBox="0 0 760 260"
            class="block h-64 w-full"
            role="img"
            aria-label="Spectrum preview"
          >
            <line x1="24" y1="236" x2="736" y2="236" stroke="currentColor" class="text-gray-300" />
            <line x1="24" y1="24" x2="24" y2="236" stroke="currentColor" class="text-gray-300" />

            <polyline
              :points="previewPolyline"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="text-blue-600"
            />
          </svg>
        </div>

        <p class="mt-2 text-xs text-gray-500">
          Preview only. The numerical values are not modified, normalized, smoothed, or reordered.
        </p>
      </div>

      <!-- Data preview -->
      <div
        v-if="validation.previewRows.length > 0"
        class="rounded-lg border border-gray-200 bg-white p-5"
      >
        <h3 class="mb-3 text-lg font-semibold text-gray-900">
          First valid rows
        </h3>

        <div class="overflow-x-auto">
          <table class="min-w-full border-collapse text-sm">
            <thead>
              <tr class="bg-gray-50 text-left text-gray-700">
                <th class="border border-gray-200 px-3 py-2">File line</th>
                <th class="border border-gray-200 px-3 py-2">X</th>
                <th class="border border-gray-200 px-3 py-2">Y</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="point in validation.previewRows"
                :key="`${point.lineNumber}-${point.x}-${point.y}`"
              >
                <td class="border border-gray-200 px-3 py-2">
                  {{ point.lineNumber }}
                </td>
                <td class="border border-gray-200 px-3 py-2">
                  {{ point.x }}
                </td>
                <td class="border border-gray-200 px-3 py-2">
                  {{ point.y }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Metadata -->
    <div class="space-y-6">
      <h3 class="text-lg font-semibold text-gray-900">
        2. Describe and organize the spectrum
      </h3>

      <div>
        <label class="text-blue-700 block mb-2 font-medium">
          Display Name
        </label>

        <input
          v-model="displayName"
          type="text"
          :disabled="isUploading"
          placeholder="Example: Fayalite"
          class="w-full p-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        />

        <p class="mt-2 text-xs text-gray-500">
          This is the user-facing spectrum name. The original uploaded filename is still preserved separately.
        </p>
      </div>

      <div>
        <label class="text-blue-700 block mb-2 font-medium">
          Category
        </label>

        <select
          v-model="category"
          :disabled="isUploading"
          class="w-full p-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <option value="">Select Category</option>
          <option
            v-for="cat in categoryOptions"
            :key="cat.id"
            :value="cat.id"
          >
            {{ cat.name_en }}
          </option>
        </select>
      </div>

      <div>
        <label class="text-blue-700 block mb-2 font-medium">
          Subcategory
        </label>

        <select
          v-model="subcategory"
          :disabled="!category || isUploading"
          class="w-full p-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <option value="">Select Subcategory</option>
          <option
            v-for="sub in filteredSubcategories"
            :key="sub.id"
            :value="sub.id"
          >
            {{ sub.name_en }}
          </option>
        </select>
      </div>

      <div>
        <label class="text-blue-700 block mb-2 font-medium">
          Folder
        </label>

        <select
          v-model="folder"
          :disabled="isUploading"
          class="w-full p-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <option value="">Select Folder</option>
          <option
            v-for="item in folderOptions"
            :key="item.id"
            :value="item.id"
          >
            {{ item.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Review button -->
    <button
      v-if="!isReviewing"
      type="button"
      :disabled="!canReview"
      @click="reviewImport"
      class="w-full rounded-md bg-blue-600 px-6 py-3 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
    >
      Review Import
    </button>

    <!-- Final confirmation -->
    <div
      v-if="isReviewing && validation"
      class="rounded-lg border-2 border-blue-200 bg-blue-50 p-5"
    >
      <h3 class="text-lg font-semibold text-blue-900">
        3. Confirm import
      </h3>

      <div class="mt-4 grid gap-3 text-sm md:grid-cols-2">
        <div>
          <span class="font-medium text-gray-700">Display name:</span>
          {{ displayName.trim() }}
        </div>

        <div>
          <span class="font-medium text-gray-700">Original file:</span>
          {{ selectedFile?.name }}
        </div>

        <div>
          <span class="font-medium text-gray-700">Category:</span>
          {{ selectedCategoryName }}
        </div>

        <div>
          <span class="font-medium text-gray-700">Subcategory:</span>
          {{ selectedSubcategoryName }}
        </div>

        <div>
          <span class="font-medium text-gray-700">Folder:</span>
          {{ selectedFolderName }}
        </div>

        <div>
          <span class="font-medium text-gray-700">Valid points:</span>
          {{ validation.validPointCount.toLocaleString() }}
        </div>

        <div>
          <span class="font-medium text-gray-700">X range:</span>
          {{ validation.xMin }} to {{ validation.xMax }}
        </div>

        <div>
          <span class="font-medium text-gray-700">Skipped rows:</span>
          {{ validation.invalidRowCount.toLocaleString() }}
        </div>

        <div>
          <span class="font-medium text-gray-700">Delimiter:</span>
          {{ validation.delimiterName }}
        </div>
      </div>

      <div class="mt-5 flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          :disabled="isUploading"
          @click="cancelReview"
          class="w-full rounded-md bg-gray-500 px-5 py-3 text-white hover:bg-gray-600 disabled:opacity-50"
        >
          Back
        </button>

        <button
          type="button"
          :disabled="isUploading"
          @click="confirmImport"
          class="w-full rounded-md bg-green-600 px-5 py-3 text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {{ isUploading ? 'Importing...' : 'Confirm Import' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>
